<script setup lang="ts">
import { TRANSITION_SOURCE } from '#shared/constant/constant'
import type { BodyTypesResponse } from '#shared/interfaces/top/bodyTypes'
import {
  getBodyTypeTab,
  type BodyTypeTabTitle,
} from '@/constant/brandCarParams'
import { resolveBodyTypeIconUrl } from '@/utils/bodyTypeIcon'

const { tabTitle, contents } = defineProps<{
  tabTitle: BodyTypeTabTitle
  contents: BodyTypesResponse
}>()
// タブ固有の値(fuel_cd / アイコンのサブディレクトリ)は定数側に集約している
const bodyTypeTab = computed(() => getBodyTypeTab(tabTitle))
const catalogSearchBodyTypeFlg: Ref<boolean> = useState(
  'catalog_search_body_type_flg',
  () => true
)
const clickTag = ref(false)
/**
 * ボディタイプアイコンのファイル名はbody_sub_cdに変更したものを保持している
 */
const generateBodyTypeAssetImagePath = (bodySubCd: string | undefined) =>
  resolveBodyTypeIconUrl(bodyTypeTab.value.iconDir, bodySubCd)

const allSearch = () => {
  catalogSearchBodyTypeFlg.value = true

  const fuelCd = bodyTypeTab.value.fuelCd
  if (fuelCd) {
    navigateTo({
      path: 'body-type-selection',
      query: { tab_title: tabTitle, fuel_cd: fuelCd },
    })
  } else {
    navigateTo({
      path: 'body-type-selection',
      query: { tab_title: tabTitle },
    })
  }
}
const nuxtApp = useNuxtApp()
const sendEvent = (bodySubCd: string) => {
  if (!clickTag.value) {
    clickTag.value = true

    const fuelCd = bodyTypeTab.value.fuelCd

    nuxtApp.$sendEventGaTag('CatalogCatalogSearchTap')

    navigateTo({
      path: '/car-model-list',
      query: {
        body_sub_cd: bodySubCd,
        fuel_cd: fuelCd,
        transition_source: TRANSITION_SOURCE.catalog_search,
      },
    })
  }
}

const emit = defineEmits<{
  (event: 'changeDisplayIndex', displayIndex: number): void
}>()

const changeDisplayIndex = (e: number) => {
  nuxtApp.$sendEventGaTag('CatalogCatalogSearchTap')
  emit('changeDisplayIndex', e)
}

onMounted(() => {
  clickTag.value = false
})
</script>

<template>
  <div v-if="contents" class="item-brand-car">
    <div v-for="x in Math.ceil(contents.data.length / 4)" :key="x" class="row">
      <div v-for="i in 4" :key="i" class="car-logo">
        <div
          v-if="
            contents.data[i + (x - 1) * 4 - 1] &&
            1 === contents.data[i + (x - 1) * 4 - 1]!.items.length
          "
          @click="sendEvent(contents.data[i + (x - 1) * 4 - 1]!.body_sub_cd)"
        >
          <a data-wpel-link="internal">
            <img
              :src="
                generateBodyTypeAssetImagePath(
                  contents.data[i + (x - 1) * 4 - 1]!.body_sub_cd
                )
              "
              onerror="
                this.src = 'https://img.goo-net.com/goo/usedcar/nophoto_big.jpg'
              "
              :class="[x === 1 ? 'first-line' : '']"
            />
          </a>
        </div>
        <a
          v-if="
            contents.data[i + (x - 1) * 4 - 1] &&
            1 !== contents.data[i + (x - 1) * 4 - 1]!.items.length
          "
          data-wpel-link="internal"
          data-bs-toggle="modal"
          data-bs-target="#bodyTypeSubModal"
          @click="changeDisplayIndex(i + (x - 1) * 4 - 1)"
        >
          <img
            :src="
              generateBodyTypeAssetImagePath(
                contents.data[i + (x - 1) * 4 - 1]!.body_sub_cd
              )
            "
            onerror="
              this.src = 'https://img.goo-net.com/goo/usedcar/nophoto_big.jpg'
            "
            :class="[x === 1 ? 'first-line' : '']"
          />
        </a>
        <span
          v-if="contents.data[i + (x - 1) * 4 - 1]"
          class="name"
          style="white-space: pre-wrap"
        >
          {{
            contents.data[i + (x - 1) * 4 - 1]!.body_type_title.replace(
              'オープン・カブリオレ・コンバーチブル',
              'オープン・\nカブリオレ・\nコンバーチブル'
            )
              .replace('ステーションワゴン', 'ステーション\nワゴン')
              .replace('ハッチバック', 'コンパクトカー')
          }}
        </span>
        <a v-else data-wpel-link="internal">
          <span />
        </a>
      </div>
    </div>
    <div class="container text-center btn-area" @click="allSearch()">
      <div class="btn-all-search shadow-sm rounded">
        <div class="btn-column" />
        <div class="btn-column btn-title">すべて見る</div>
        <div class="btn-column arrow-right">
          <img src="@/assets/image/icon/arrow_right.svg" />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.item-brand-car {
  background-color: #ffffff;
  width: 100%;
  height: auto;
}

.row {
  --bs-gutter-x: 0;
}

a {
  width: 56px;
}

.car-logo {
  width: auto;
  margin: 0 auto;
}

.first-line {
  margin-top: 21px;
}

.car-logo img {
  width: 56px;
  height: 56px;
  display: block;
  box-shadow: 1px 1px 1px #0000001a;
  margin-left: auto;
  margin-right: auto;
  border-radius: 10px;
  margin-bottom: 10px;
  padding: 6px;
}

.car-logo span {
  width: 80px;
  display: block;
  box-shadow: 0 0 0 0 #0000001a;
  margin-bottom: 10px;
}

.car-logo .name {
  font-size: 11px;
  font-weight: bold;
  width: 81px;
  text-align: center;
  margin-bottom: 12px;
  color: #333333;
  padding: 0px;
}

.btn-area {
  height: 74px;
  margin-top: 15px;
}

.btn-all-search {
  display: flex;
  font-weight: bold;
  height: 42px;
  width: 100%;
  background: #ffffff;
  align-items: center;
}

.btn-column {
  width: 33.3% !important;
}

.arrow-right {
  display: flex;
  justify-content: flex-end;
  padding-right: 16px;
}

.arrow-right img {
  height: 19px;
  width: 19px;
}

.btn-title {
  font-size: 12px;
  font-weight: bold;
}
</style>
